import { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import SEO from '@/components/feature/SEO';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categories, categoryMap } from '@/mocks/categories';
import { getResourcesByCategory, searchResources, resources as allResourcesData, type Resource } from '@/mocks/resources';

const ITEMS_PER_PAGE = 12;

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const [currentPage, setCurrentPage] = useState(1);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const isAll = slug === 'all';
  const currentCategory = isAll ? null : categoryMap[slug || ''];
  const isSearch = !!query;

  let allResources: Resource[] = [];
  if (isSearch) {
    allResources = searchResources(query);
  } else if (isAll) {
    allResources = [...allResourcesData].sort((a: Resource, b: Resource) =>
      new Date(b.date).getTime() - new Date(a.date).getTime()
    );
  } else if (currentCategory) {
    allResources = getResourcesByCategory(slug!);
  } else {
    allResources = [];
  }

  const totalPages = Math.ceil(allResources.length / ITEMS_PER_PAGE) || 1;
  const paginatedResources = allResources.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [slug, query]);

  const copyMagnet = async (resource: Resource) => {
    try {
      await navigator.clipboard.writeText(resource.magnet);
      setCopiedId(resource.id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      // fallback
    }
  };

  const getPageNumbers = useCallback(() => {
    const pages: (number | string)[] = [];
    const maxVisible = 7;
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('...');
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      for (let i = start; i <= end; i++) pages.push(i);
      if (currentPage < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  }, [currentPage, totalPages]);

  const pageTitle = isSearch
    ? `搜索：${query}`
    : isAll
      ? '全部资源'
      : currentCategory
        ? `${currentCategory.name}资源`
        : '分类';

  const pageDescription = isSearch
    ? `磁力猫搜索结果：${query}，共找到 ${allResources.length} 个相关资源。`
    : isAll
      ? '磁力猫全部资源列表，包含影视、音乐、软件、电子书、游戏、动漫、纪录片、学习资料等海量磁力链接资源。'
      : currentCategory
        ? currentCategory.description
        : '';

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    ...(isAll ? [{ label: '全部资源' }] : []),
    ...(currentCategory ? [{ label: currentCategory.name }] : []),
    ...(isSearch ? [{ label: `搜索：${query}` }] : []),
  ];

  return (
    <>
      <SEO
        title={`${pageTitle} - 磁力猫资源搜索`}
        description={pageDescription}
        keywords={`磁力猫,${pageTitle},磁力链接搜索,BT磁力搜索,资源下载`}
        canonicalUrl={`https://www.ciligod.com/category/${slug || 'all'}${query ? `?q=${encodeURIComponent(query)}` : ''}`}
      />
      <div className="min-h-screen bg-background-50 pt-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
          <Breadcrumb items={breadcrumbItems} />

          <div className="mt-6 mb-10">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
              {isSearch ? `搜索结果：${query}` : isAll ? '全部资源' : currentCategory?.name}
            </h1>
            {!isSearch && (
              <p className="mt-2 text-sm text-foreground-500">
                {isAll
                  ? '浏览全部资源分类，发现你感兴趣的内容'
                  : currentCategory?.description}
                　共 <strong className="text-foreground-700">{allResources.length}</strong> 个资源
              </p>
            )}
            {isSearch && (
              <p className="mt-2 text-sm text-foreground-500">
                关于 "<strong>{query}</strong>" 的搜索结果，共找到 <strong>{allResources.length}</strong> 个相关资源
              </p>
            )}
          </div>

          {!isSearch && (
            <div className="flex flex-wrap gap-2 mb-8">
              <Link
                to="/category/all"
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  isAll
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                全部
              </Link>
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    slug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          )}

          {allResources.length === 0 ? (
            <div className="text-center py-20">
              <span className="w-16 h-16 flex items-center justify-center mx-auto rounded-2xl bg-background-100 text-foreground-300 text-2xl mb-4">
                <i className="ri-search-line"></i>
              </span>
              <p className="text-foreground-500 text-sm">没有找到相关资源，请尝试其他关键词</p>
            </div>
          ) : (
            <>
              <div className="bg-background-50 rounded-xl border border-background-200/60 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-background-200/60 bg-background-100">
                        <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 whitespace-nowrap">
                          资源名称
                        </th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 whitespace-nowrap hidden md:table-cell">
                          分类
                        </th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 whitespace-nowrap hidden sm:table-cell">
                          大小
                        </th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 whitespace-nowrap hidden lg:table-cell">
                          日期
                        </th>
                        <th className="text-center px-4 py-3 text-xs font-semibold text-foreground-500 whitespace-nowrap">
                          做种
                        </th>
                        <th className="text-center px-4 py-3 text-xs font-semibold text-foreground-500 whitespace-nowrap hidden sm:table-cell">
                          下载
                        </th>
                        <th className="text-right px-4 py-3 text-xs font-semibold text-foreground-500 whitespace-nowrap">
                          操作
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedResources.map((resource) => {
                        const cat = categoryMap[resource.category];
                        return (
                          <tr
                            key={resource.id}
                            className="border-b border-background-200/60 hover:bg-background-100/50 transition-colors"
                          >
                            <td className="px-4 py-3.5">
                              <Link
                                to={`/article/${resource.id}`}
                                className="text-sm font-medium text-foreground-900 hover:text-primary-600 transition-colors line-clamp-1 cursor-pointer"
                              >
                                {resource.title}
                              </Link>
                              <div className="flex flex-wrap gap-1.5 mt-1">
                                {resource.tags.slice(0, 3).map((tag) => (
                                  <span
                                    key={tag}
                                    className="px-1.5 py-0.5 rounded text-[10px] bg-secondary-100 text-secondary-700"
                                  >
                                    {tag}
                                  </span>
                                ))}
                              </div>
                            </td>
                            <td className="px-4 py-3.5 hidden md:table-cell">
                              <span className="text-xs text-foreground-500 whitespace-nowrap">
                                {cat?.name || resource.category}
                              </span>
                            </td>
                            <td className="px-4 py-3.5 hidden sm:table-cell">
                              <span className="text-xs text-foreground-500 whitespace-nowrap">
                                {resource.size}
                              </span>
                            </td>
                            <td className="px-4 py-3.5 hidden lg:table-cell">
                              <span className="text-xs text-foreground-400 whitespace-nowrap">
                                {resource.date}
                              </span>
                            </td>
                            <td className="px-4 py-3.5 text-center">
                              <span className="text-xs font-medium text-accent-600 whitespace-nowrap">
                                {resource.seeders.toLocaleString()}
                              </span>
                            </td>
                            <td className="px-4 py-3.5 text-center hidden sm:table-cell">
                              <span className="text-xs text-foreground-400 whitespace-nowrap">
                                {resource.leechers.toLocaleString()}
                              </span>
                            </td>
                            <td className="px-4 py-3.5 text-right">
                              <button
                                onClick={() => copyMagnet(resource)}
                                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-primary-500 hover:bg-primary-600 text-background-50 text-xs font-medium transition-colors whitespace-nowrap cursor-pointer"
                              >
                                <i className={copiedId === resource.id ? 'ri-check-line' : 'ri-file-copy-line'}></i>
                                <span className="hidden sm:inline">
                                  {copiedId === resource.id ? '已复制' : '复制'}
                                </span>
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-1.5 mt-8">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="w-9 h-9 flex items-center justify-center rounded-lg text-sm text-foreground-500 hover:bg-background-100 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
                  >
                    <i className="ri-arrow-left-s-line"></i>
                  </button>
                  {getPageNumbers().map((page, idx) =>
                    page === '...' ? (
                      <span key={`dots-${idx}`} className="w-9 h-9 flex items-center justify-center text-sm text-foreground-400">
                        ...
                      </span>
                    ) : (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page as number)}
                        className={`w-9 h-9 flex items-center justify-center rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                          currentPage === page
                            ? 'bg-primary-500 text-background-50'
                            : 'text-foreground-600 hover:bg-background-100'
                        }`}
                      >
                        {page}
                      </button>
                    )
                  )}
                  <button
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="w-9 h-9 flex items-center justify-center rounded-lg text-sm text-foreground-500 hover:bg-background-100 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
                  >
                    <i className="ri-arrow-right-s-line"></i>
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}